import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FlatList,
  ListRenderItem,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { ChevronLeft, ChevronRight } from "lucide-react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { Credentials } from "../storage/credentials";
import { JellyfinItem } from "../api/jellyfin";
import { MediaCard } from "./MediaCard";
import { getBrowseVisualTokens, spacing, typography , systemSansFont} from '../theme/tokens';
import { LandscapeMediaCard } from "./LandscapeMediaCard";
import { colors } from "../theme/tokens";
import { FocusablePressable } from "./FocusablePressable";
import { getFocusModality } from "../tvNavigation/registry";
import { getFocusRoute, getFocusTransitionCount } from "../tvNavigation/registry";
import { performTVFeedback } from "../tvNavigation/feedback";
import { useNativePreferences } from "../storage/preferences";
import { SimplePosterCard } from "./SimplePosterCard";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";

export type MediaShelfPresentation = "standard" | "continue";

type Props = {
  title: string;
  shelfKey?: string;
  items: JellyfinItem[];
  credentials: Credentials;
  onItemPress: (item: JellyfinItem) => void;
  onItemPlay?: (item: JellyfinItem) => void;
  onTitlePress?: () => void;
  landscape?: boolean;
  presentation?: MediaShelfPresentation;
  hideLabels?: boolean;
  contentTopPadding?: number;
  posterWidth?: number;
  listHorizontalPadding?: number;
  cardGap?: number;
  simple?: boolean;
  posterHeight?: number;
  bounded?: boolean;
  onItemIntentChange?: (item: JellyfinItem, active: boolean) => void;
  focusEntryUpId?: string;
  focusEntryDownId?: string;
  onRowFocus?: (active: boolean) => void;
};

const savedShelfOffsets = new Map<string, number>();
const EDGE_FADE_WIDTH = 132;
const EDGE_FADE_BLEED = 10;

function MediaShelfComponent({
  title,
  shelfKey,
  items,
  credentials,
  onItemPress,
  onItemPlay,
  onTitlePress,
  landscape = false,
  presentation = "standard",
  hideLabels = false,
  contentTopPadding,
  posterWidth,
  listHorizontalPadding,
  cardGap,
  simple = false,
  posterHeight,
  bounded = false,
  onItemIntentChange,
  focusEntryUpId,
  focusEntryDownId,
  onRowFocus,
}: Props) {
  const { width } = useReferenceWindowDimensions();
  const preferences = useNativePreferences();
  const browseVisuals = getBrowseVisualTokens(width);
  const television = browseVisuals.viewport === "television";
  const pagePadding = browseVisuals.pagePadding;
  const horizontalPadding = listHorizontalPadding ?? pagePadding;
  const cardWidth =
    posterWidth ??
    (landscape
      ? browseVisuals.viewport === "television" ? 340 : width > 760 ? 305 : 180
      : browseVisuals.viewport === "television" ? 220 : width > 760 ? 190 : 110);
  const listRef = useRef<FlatList<JellyfinItem>>(null);
  const cardViewportHeight = posterHeight ?? (landscape ? Math.round(cardWidth * 0.56) : Math.round(cardWidth * 1.5));
  const edgeFadeHeight = cardViewportHeight + EDGE_FADE_BLEED * 2;
  const resolvedGap = cardGap ?? (landscape ? spacing.sm : cardWidth <= 110 ? 6 : 8);
  const itemStride = cardWidth + resolvedGap;
  const identity = shelfKey ?? title;
  const initialOffset = useRef(savedShelfOffsets.get(identity) ?? 0).current;
  const [pageIndex, setPageIndex] = useState(() =>
    Math.max(0, Math.round(initialOffset / Math.max(1, width - horizontalPadding * 2))),
  );
  const [offset, setOffset] = useState(initialOffset);
  const [controlsVisible, setControlsVisible] = useState(false);
  const controlsExitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const itemIntentState = useRef(
    new Map<string, { focused: boolean; hovered: boolean }>(),
  );
  const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);
  const lastFocusedItem = useRef<string | null>(null);
  const lastFocusTransitionCount = useRef(getFocusTransitionCount());
  const itemTypes = useMemo(() => new Set(items.map((item) => item.Type).filter(Boolean)), [items]);
  const showTypeChip = itemTypes.has("Movie") && itemTypes.has("Series");
  const viewportWidth = Math.max(1, width - horizontalPadding * 2);
  const contentWidth = Math.max(0, items.length * itemStride - resolvedGap);
  const pageCount = Math.max(1, Math.ceil(contentWidth / viewportWidth));
  const maxOffset = Math.max(0, contentWidth - viewportWidth);
  const canScrollLeft = pageIndex > 0 && offset > 10;
  const canScrollRight = offset < maxOffset - 10;
  useEffect(
    () => () => {
      if (controlsExitTimer.current) clearTimeout(controlsExitTimer.current);
    },
    [],
  );
  const setControlsActive = useCallback((nextActive: boolean) => {
    if (controlsExitTimer.current) {
      clearTimeout(controlsExitTimer.current);
      controlsExitTimer.current = null;
    }
    if (nextActive) setControlsVisible(true);
    else
      controlsExitTimer.current = setTimeout(
        () => setControlsVisible(false),
        120,
      );
  }, []);
  const updatePage = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const nextOffset = event.nativeEvent.contentOffset.x;
    savedShelfOffsets.set(identity, nextOffset);
    if (savedShelfOffsets.size > 100) {
      const oldest = savedShelfOffsets.keys().next().value;
      if (oldest) savedShelfOffsets.delete(oldest);
    }
    setOffset(nextOffset);
    setPageIndex(
      Math.min(pageCount - 1, Math.round(nextOffset / viewportWidth)),
    );
  }, [identity, pageCount, viewportWidth]);
  const scrollPage = useCallback((direction: -1 | 1) => {
    const nextOffset = Math.max(
      0,
      Math.min(maxOffset, offset + direction * viewportWidth * 0.82),
    );
    savedShelfOffsets.set(identity, nextOffset);
    setOffset(nextOffset);
    setPageIndex(Math.min(pageCount - 1, Math.round(nextOffset / viewportWidth)));
    listRef.current?.scrollToOffset({ offset: nextOffset, animated: true });
  }, [identity, maxOffset, offset, pageCount, viewportWidth]);
  const initialRenderCount = Math.min(
    items.length,
    television
      ? Math.max(3, Math.min(5, Math.ceil(viewportWidth / itemStride)))
      : Math.max(2, Math.ceil(viewportWidth / itemStride) + 1),
  );
  const getItemLayout = useCallback((_: ArrayLike<JellyfinItem> | null | undefined, index: number) => ({
    length: itemStride,
    offset: itemStride * index,
    index,
  }), [itemStride]);
  const renderItem = useCallback<ListRenderItem<JellyfinItem>>(({ item, index }) => {
    const itemKey = item.Id ?? `${title}-${index}`;
    const previousItem = index > 0 ? items[index - 1] : undefined;
    const nextItem = index < items.length - 1 ? items[index + 1] : undefined;
    const focusDestinations = {
      ...(focusEntryUpId ? { up: focusEntryUpId } : {}),
      ...(focusEntryDownId ? { down: focusEntryDownId } : {}),
      ...(previousItem?.Id ? { left: `shelf.${identity}.${previousItem.Id}` } : {}),
      ...(nextItem?.Id ? { right: `shelf.${identity}.${nextItem.Id}` } : {}),
    };
    const updateItemIntent = (kind: "focused" | "hovered", active: boolean) => {
      const state = itemIntentState.current.get(itemKey) ?? { focused: false, hovered: false };
      state[kind] = active;
      itemIntentState.current.set(itemKey, state);
      onItemIntentChange?.(item, state.focused || state.hovered);
    };
    const handleHover = (active: boolean) => {
      // A remote-driven TV surface has no pointer hover state. Avoid updating
      // shelf state on every focus transition; that invalidates the FlatList
      // render callback and can produce long UI-thread frame spikes.
      if (television) return;
      setHoveredItemKey((current) => active ? itemKey : current === itemKey ? null : current);
      if (active) setControlsActive(true);
      updateItemIntent("hovered", active);
    };
    const handleFocus = (active: boolean) => {
      if (active) {
        onRowFocus?.(true);
        const transitions = getFocusTransitionCount();
        const previousItem = lastFocusedItem.current;
        const previousShelf = previousItem?.split(':')[0];
        const previousKey = previousItem?.slice((previousShelf?.length ?? 0) + 1);
        const controllerNavigation = getFocusModality() === 'gamepad' || getFocusModality() === 'native-tv';
        // Some native TV builds move a horizontal FlatList child visually but
        // omit the semantic focus event. Restore only that missing item cue;
        // row transitions already increment the registry count and are left
        // untouched to avoid duplicate sounds.
        if (controllerNavigation && previousShelf === identity && previousKey !== itemKey && transitions === lastFocusTransitionCount.current) {
          performTVFeedback({ cue: 'focus-item', modality: getFocusModality(), route: getFocusRoute(), settings: { uiSounds: preferences.uiSounds, controllerHaptics: preferences.controllerHaptics } });
        }
        lastFocusedItem.current = `${identity}:${itemKey}`;
        lastFocusTransitionCount.current = transitions;
      }
      const controllerNavigation = getFocusModality() === 'gamepad' || getFocusModality() === 'native-tv';
      if (controllerNavigation) {
        if (controlsExitTimer.current) clearTimeout(controlsExitTimer.current);
        setControlsVisible(false);
      } else setControlsActive(active);
      updateItemIntent("focused", active);
    };
    const destinations = Object.keys(focusDestinations).length ? focusDestinations : undefined;
    return simple ? (
      <SimplePosterCard focusGroup={`shelf.${identity}`} item={item} credentials={credentials} width={cardWidth} height={cardViewportHeight} gap={cardGap} onPress={() => onItemPress(item)} focusDestinations={destinations} />
    ) : landscape ? (
      <LandscapeMediaCard focusGroup={`shelf.${identity}`} item={item} credentials={credentials} width={cardWidth} presentation={presentation === "continue" ? "continue" : "default"} hovered={hoveredItemKey === itemKey} onHoverChange={handleHover} onFocusChange={handleFocus} onPress={() => onItemPress(item)} onPlay={() => (onItemPlay ?? onItemPress)(item)} focusDestinations={destinations} />
    ) : (
      <MediaCard focusGroup={`shelf.${identity}`} item={item} credentials={credentials} width={cardWidth} gap={cardGap} hideLabels={hideLabels} showTypeChip={showTypeChip} hovered={hoveredItemKey === itemKey} onHoverChange={handleHover} onFocusChange={handleFocus} onPress={() => onItemPress(item)} onPlay={() => (onItemPlay ?? onItemPress)(item)} focusDestinations={destinations} />
    );
  }, [cardGap, cardViewportHeight, cardWidth, credentials, focusEntryDownId, focusEntryUpId, hideLabels, identity, items, landscape, onItemIntentChange, onItemPlay, onItemPress, onRowFocus, presentation, setControlsActive, showTypeChip, simple, television, title, hoveredItemKey]);
  if (!items.length) return null;
  return (
    <Pressable
      accessible={false}
      focusable={false}
      onHoverIn={() => setControlsActive(true)}
      onHoverOut={() => setControlsActive(false)}
      style={[styles.root, width <= 760 && styles.rootMobile, browseVisuals.viewport === "television" && styles.rootTelevision]}
    >
      <View style={[styles.header, { paddingHorizontal: horizontalPadding }]}>
        {onTitlePress ? <>
          <FocusablePressable
            accessibilityLabel={`Open ${title}`}
            onPress={onTitlePress}
            style={(focused, pressed, hovered) => [styles.titleAction, (focused || hovered) && styles.titleActionActive, focused && styles.titleActionFocused, pressed && styles.titleActionPressed]}
          >
            <Text style={[typography.sectionTitle, width <= 760 ? styles.titleMobile : styles.titleDesktop, { fontSize: browseVisuals.shelfTitleSize, lineHeight: browseVisuals.shelfTitleLineHeight }]}>{title}</Text>
            <ChevronRight color="rgba(255,255,255,0.66)" size={browseVisuals.viewport === "television" ? 24 : 18} strokeWidth={2.2} />
          </FocusablePressable>
          <View style={styles.headerTitle} />
        </> : <Text
          style={[typography.sectionTitle, width <= 760 ? styles.titleMobile : styles.titleDesktop, styles.headerTitle, { fontSize: browseVisuals.shelfTitleSize, lineHeight: browseVisuals.shelfTitleLineHeight }]}
        >{title}</Text>}
        {width > 760 && pageCount > 1 && controlsVisible ? (
          <View style={styles.pageDots}>
            {Array.from({ length: Math.min(pageCount, 8) }).map((_, index) => (
              <View
                key={index}
                style={[
                  styles.pageDot,
                  index === Math.min(pageIndex, 7) && styles.pageDotActive,
                ]}
              />
            ))}
          </View>
        ) : null}
      </View>
      <Pressable
        accessible={false}
        focusable={false}
        onHoverIn={() => setControlsActive(true)}
        onHoverOut={() => setControlsActive(false)}
        style={[
          styles.listShell,
          bounded && [styles.listShellBounded, { marginHorizontal: horizontalPadding }],
        ]}
      >
        <FlatList
          ref={listRef}
          horizontal
          data={items}
          contentOffset={{ x: initialOffset, y: 0 }}
          keyExtractor={(item, index) => item.Id ?? `${title}-${index}`}
          showsHorizontalScrollIndicator={false}
          initialNumToRender={initialRenderCount}
          maxToRenderPerBatch={television ? 3 : Math.max(2, Math.min(initialRenderCount, 4))}
          updateCellsBatchingPeriod={television ? 48 : 32}
          // A one-screen TV window bounds decoded artwork retained by the
          // nested horizontal lists while still allowing FlatList to advance
          // focus and mount the next page on demand.
          windowSize={television ? 1 : 3}
          // Detaching off-screen cards materially reduces native TV view
          // traversal while FlatList preserves their data and focus order.
          removeClippedSubviews={television}
          getItemLayout={getItemLayout}
          onMomentumScrollEnd={updatePage}
          onScrollEndDrag={updatePage}
          contentContainerStyle={{
            paddingHorizontal: bounded ? 0 : horizontalPadding,
            paddingTop: contentTopPadding ?? (landscape ? 17 : 20),
            paddingBottom: landscape ? spacing.sm : 20,
          }}
          renderItem={renderItem}
        />
        {width > 760 && controlsVisible && canScrollLeft ? (
          <View pointerEvents="box-none" style={[styles.edgeButton, styles.edgeLeft]}>
                <Svg
                  pointerEvents="none"
                  width={EDGE_FADE_WIDTH}
                  height={edgeFadeHeight}
                  style={[styles.edgeFade, { top: -EDGE_FADE_BLEED }]}
                >
                  <Defs>
                    <LinearGradient
                      id="shelfEdgeLeft"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0"
                    >
                      <Stop offset="0" stopColor="#000" stopOpacity="0.76" />
                      <Stop offset="0.38" stopColor="#000" stopOpacity="0.28" />
                      <Stop offset="0.72" stopColor="#000" stopOpacity="0.06" />
                      <Stop offset="1" stopColor="#000" stopOpacity="0" />
                    </LinearGradient>
                  </Defs>
                  <Rect width={EDGE_FADE_WIDTH} height={edgeFadeHeight} fill="url(#shelfEdgeLeft)" />
                </Svg>
                <FocusablePressable
                  accessibilityLabel={`Scroll ${title} left`}
                  onHoverIn={() => setControlsActive(true)}
                  onHoverOut={() => setControlsActive(false)}
                  onFocus={() => setControlsActive(true)}
                  onBlur={() => setControlsActive(false)}
                  onPress={() => scrollPage(-1)}
                  controllerFocusable={false}
                  style={(focused) => [styles.edgeControl, styles.edgeControlLeft, focused && styles.edgeFocused]}
                >
                  <ChevronLeft
                    color={colors.text}
                    size={22}
                    strokeWidth={2.2}
                  />
                </FocusablePressable>
          </View>
        ) : null}
        {width > 760 && controlsVisible && canScrollRight ? (
          <View pointerEvents="box-none" style={[styles.edgeButton, styles.edgeRight]}>
                <Svg
                  pointerEvents="none"
                  width={EDGE_FADE_WIDTH}
                  height={edgeFadeHeight}
                  style={[styles.edgeFade, { top: -EDGE_FADE_BLEED }]}
                >
                  <Defs>
                    <LinearGradient
                      id="shelfEdgeRight"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="0"
                    >
                      <Stop offset="0" stopColor="#000" stopOpacity="0" />
                      <Stop offset="0.28" stopColor="#000" stopOpacity="0.06" />
                      <Stop offset="0.62" stopColor="#000" stopOpacity="0.28" />
                      <Stop offset="1" stopColor="#000" stopOpacity="0.76" />
                    </LinearGradient>
                  </Defs>
                  <Rect width={EDGE_FADE_WIDTH} height={edgeFadeHeight} fill="url(#shelfEdgeRight)" />
                </Svg>
                <FocusablePressable
                  accessibilityLabel={`Scroll ${title} right`}
                  onHoverIn={() => setControlsActive(true)}
                  onHoverOut={() => setControlsActive(false)}
                  onFocus={() => setControlsActive(true)}
                  onBlur={() => setControlsActive(false)}
                  onPress={() => scrollPage(1)}
                  controllerFocusable={false}
                  style={(focused) => [styles.edgeControl, styles.edgeControlRight, focused && styles.edgeFocused]}
                >
                  <ChevronRight
                    color={colors.text}
                    size={22}
                    strokeWidth={2.2}
                  />
                </FocusablePressable>
          </View>
        ) : null}
      </Pressable>
    </Pressable>
  );
}

export const MediaShelf = React.memo(MediaShelfComponent, (previous, next) =>
  previous.shelfKey === next.shelfKey &&
  previous.title === next.title &&
  previous.items === next.items &&
  previous.credentials.accessToken === next.credentials.accessToken &&
  previous.credentials.serverUrl === next.credentials.serverUrl &&
  previous.landscape === next.landscape &&
  previous.presentation === next.presentation &&
  previous.onTitlePress === next.onTitlePress &&
  previous.hideLabels === next.hideLabels &&
  previous.contentTopPadding === next.contentTopPadding &&
  previous.posterWidth === next.posterWidth &&
  previous.listHorizontalPadding === next.listHorizontalPadding &&
  previous.cardGap === next.cardGap &&
  previous.simple === next.simple &&
  previous.posterHeight === next.posterHeight &&
  previous.bounded === next.bounded,
);

const styles = StyleSheet.create({
  root: { marginBottom: 8, paddingTop: 0 },
  rootMobile: { paddingTop: 5 },
  rootTelevision: { marginBottom: 14 },
  header: { flexDirection: "row", alignItems: "center" },
  headerTitle: { flex: 1 },
  titleAction: { alignSelf: "flex-start", flexDirection: "row", alignItems: "center", gap: 5, borderWidth: 2, borderColor: "transparent", borderRadius: 6, paddingHorizontal: 5 },
  titleActionActive: { opacity: 0.9 },
  titleActionFocused: { borderColor: colors.text },
  titleActionPressed: { opacity: 0.72 },
  titleDesktop: { fontFamily: systemSansFont,  fontSize: 24, lineHeight: 32, fontWeight: "700" },
  titleMobile: { fontFamily: systemSansFont,  fontSize: 18, lineHeight: 23, fontWeight: "700" },
  pageDots: { flexDirection: "row", alignItems: "center", gap: 6 },
  pageDot: {
    width: 6,
    height: 3,
    borderRadius: 99,
    backgroundColor: "rgba(255,255,255,0.26)",
  },
  pageDotActive: { width: 18, backgroundColor: "rgba(255,255,255,0.86)" },
  listShell: { position: "relative" },
  listShellBounded: { overflow: "hidden" },
  edgeButton: {
    position: "absolute",
    top: 20,
    bottom: 20,
    width: EDGE_FADE_WIDTH,
    zIndex: 20,
    elevation: 12,
    justifyContent: "center",
  },
  edgeLeft: { left: 0, alignItems: "flex-start" },
  edgeRight: { right: 0, alignItems: "flex-end" },
  edgeFade: { position: "absolute", left: 0 },
  edgeControl: {
    width: 42,
    height: 64,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.16)",
    borderRadius: 21,
    backgroundColor: "rgba(12,12,12,0.70)",
  },
  edgeControlLeft: { marginLeft: 12 },
  edgeControlRight: { marginRight: 12 },
  edgeFocused: { borderWidth: 2, borderColor: colors.text, backgroundColor: "rgba(255,255,255,0.18)", transform: [{ scale: 1.06 }] },
});
